import { useState, useEffect, useCallback, useMemo } from "react";

// Keys para localStorage
const DEFAULT_STORAGE_KEY = "tareasFilters";

// Estados disponibles para tareas
export const ESTADOS_TAREA = [
  { value: "PENDIENTE", label: "Pendiente", color: "error" },
  { value: "EN PROCESO", label: "En Proceso", color: "warning" },
  { value: "OBSERVADA SII", label: "Observada SII", color: "info" },
  { value: "TERMINADA", label: "Terminada", color: "success" },
  {
    value: "PENDIENTE DE FACTURACION",
    label: "Pend. Facturación",
    color: "secondary",
  },
  { value: "FACTURADO", label: "Facturado", color: "success" },
  {
    value: "CANCELADO O RECHAZADO",
    label: "Cancelado/Rechazado",
    color: "default",
  },
];

// Tipos de tarea disponibles
export const TIPOS_TAREA = [
  { value: "MES", label: "Mensual" },
  { value: "AÑO", label: "Anual" },
  { value: "ESPECIAL", label: "Especial" },
  { value: "INTERNA", label: "Interna" },
];

// Estado inicial de filtros
const INITIAL_FILTERS = {
  busqueda: "",
  estados: [],
  tipoTarea: "",
  fechaInicioDesde: null,
  fechaInicioHasta: null,
  fechaTerminoDesde: null,
  fechaTerminoHasta: null,
  cliente: null,
  empleado: null,
  sucursales: [], // array vacío = todas las sucursales del empleado
  // Encendido por defecto: lo habitual es querer ver primero las tareas de las
  // que uno es responsable directo. Por ser el estado normal de la pantalla no
  // cuenta como filtro activo ni lleva chip -- el switch ya muestra su estado.
  // Si se le agrega un chip, removerFiltro necesita una rama propia: su
  // fallback devuelve el valor de INITIAL_FILTERS, y aca eso es true, asi que
  // la X no lo apagaria.
  soloEncargado: true,
};

/**
 * Hook para manejar filtros de tareas
 * @param {Array} tareas - Array de tareas
 * @param {string} options.rolObtenido - Rol del usuario actual
 * @param {string} [options.storageKey] - Clave de localStorage donde persisten los filtros de esta vista. Default: "tareasFilters"
 * @returns {Object} - Estado de filtros y funciones de manipulación
 */
export default function useTareasFilters(tareas = [], options = {}) {
  const { rolObtenido, storageKey = DEFAULT_STORAGE_KEY } = options;
  const isAdmin = ["SUPER_ADMIN", "ADMIN", "SUPERVISOR"].includes(rolObtenido);

  // Cargar filtros desde localStorage o usar valores iniciales
  const [filtros, setFiltros] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Convertir strings de fecha a objetos Date si existen
        return {
          ...INITIAL_FILTERS,
          ...parsed,
          fechaInicioDesde: parsed.fechaInicioDesde
            ? new Date(parsed.fechaInicioDesde)
            : null,
          fechaInicioHasta: parsed.fechaInicioHasta
            ? new Date(parsed.fechaInicioHasta)
            : null,
          fechaTerminoDesde: parsed.fechaTerminoDesde
            ? new Date(parsed.fechaTerminoDesde)
            : null,
          fechaTerminoHasta: parsed.fechaTerminoHasta
            ? new Date(parsed.fechaTerminoHasta)
            : null,
        };
      }
    } catch (error) {
      console.warn("Error loading filters from localStorage:", error);
    }
    return INITIAL_FILTERS;
  });

  // Persistir filtros en localStorage cuando cambien
  useEffect(() => {
    try {
      const toSave = {
        ...filtros,
        // Convertir fechas a ISO string para serialización
        fechaInicioDesde: filtros.fechaInicioDesde?.toISOString() || null,
        fechaInicioHasta: filtros.fechaInicioHasta?.toISOString() || null,
        fechaTerminoDesde: filtros.fechaTerminoDesde?.toISOString() || null,
        fechaTerminoHasta: filtros.fechaTerminoHasta?.toISOString() || null,
      };
      localStorage.setItem(storageKey, JSON.stringify(toSave));
    } catch (error) {
      console.warn("Error saving filters to localStorage:", error);
    }
  }, [filtros, storageKey]);

  // Función para actualizar un filtro específico
  const updateFiltro = useCallback((campo, valor) => {
    setFiltros((prev) => ({ ...prev, [campo]: valor }));
  }, []);

  // Función para toggle de estado (chips múltiples)
  const toggleEstado = useCallback((estado) => {
    setFiltros((prev) => {
      const estadosActuales = prev.estados || [];
      const existe = estadosActuales.includes(estado);
      return {
        ...prev,
        estados: existe
          ? estadosActuales.filter((e) => e !== estado)
          : [...estadosActuales, estado],
      };
    });
  }, []);

  // Función para limpiar todos los filtros
  const limpiarFiltros = useCallback(() => {
    setFiltros(INITIAL_FILTERS);
  }, []);

  // Función para remover un filtro específico
  const removerFiltro = useCallback((campo, valor = null) => {
    setFiltros((prev) => {
      if (campo === "estados" && valor) {
        return {
          ...prev,
          estados: prev.estados.filter((e) => e !== valor),
        };
      }
      if (campo === "sucursales" && valor) {
        return {
          ...prev,
          sucursales: prev.sucursales.filter((s) => s !== valor),
        };
      }
      return {
        ...prev,
        [campo]: INITIAL_FILTERS[campo],
      };
    });
  }, []);

  // Contar filtros activos
  const filtrosActivos = useMemo(() => {
    let count = 0;
    if (filtros.busqueda) count++;
    if (filtros.estados?.length > 0) count += filtros.estados.length;
    if (filtros.tipoTarea) count++;
    if (filtros.fechaInicioDesde) count++;
    if (filtros.fechaInicioHasta) count++;
    if (filtros.fechaTerminoDesde) count++;
    if (filtros.fechaTerminoHasta) count++;
    if (filtros.cliente) count++;
    if (filtros.empleado) count++;
    if (filtros.sucursales?.length > 0) count += filtros.sucursales.length;
    return count;
  }, [filtros]);

  // Verificar si hay filtros activos
  const hayFiltrosActivos = filtrosActivos > 0;

  return {
    // Estado
    filtros,
    filtrosActivos,
    hayFiltrosActivos,

    // Acciones
    updateFiltro,
    toggleEstado,
    limpiarFiltros,
    removerFiltro,

    // Helpers
    isAdmin,
  };
}
